import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Heading,
  Text,
  Card,
  Flex,
  Button,
  Badge,
  Table,
  Spinner,
  Code,
} from '@radix-ui/themes';
import {
  TargetIcon,
  CalendarIcon,
  ActivityLogIcon,
  CheckCircledIcon,
  ArrowLeftIcon,
} from '@radix-ui/react-icons';
import * as Tooltip from '@radix-ui/react-tooltip';
import { useGetSignalQuery } from '../../graphql/queries/GetSignal.generated';
import { useGetListSummaryQuery } from '../../graphql/queries/GetListSummary.generated';
import TimeAgoTooltip from '../../components/TimeAgoTooltip';
import HashShortener from '../../components/HashShortener';
import MonetaryValueDisplay from '../../components/MonetaryValueDisplay';
import DetailPageLayout from '../../components/DetailPageLayout';
import TestSignalDialog from '../../components/TestSignalDialog';
import { ValueType } from '../../lib/workflow-defs';

type FingerprintValue = {
  valueType: string;
  value: unknown;
};

type FingerprintUnion =
  | {
    __typename: 'BlockFingerprint';
    blockField: string;
    operation: string;
    value: FingerprintValue;
  }
  | {
    __typename: 'TransactionFingerprint';
    transactionField: string;
    operation: string;
    value: FingerprintValue;
  }
  | {
    __typename: 'LogFingerprint';
    logField: string;
    operation: string;
    value: FingerprintValue;
  };

const getFingerprintType = (fingerprint: FingerprintUnion): string => {
  return fingerprint.__typename.replace('Fingerprint', '');
};

const getFingerprintField = (fingerprint: FingerprintUnion): string => {
  if ('blockField' in fingerprint) return fingerprint.blockField;
  if ('transactionField' in fingerprint) return fingerprint.transactionField;
  if ('logField' in fingerprint) return fingerprint.logField;
  return '';
};

const getOperationShorthand = (operation: string): string => {
  const operationMap: Record<string, string> = {
    'GREATER_THAN': '>',
    'GREATER_THAN_OR_EQUAL': '≥',
    'LESS_THAN': '<',
    'LESS_THAN_OR_EQUAL': '≤',
    'EQUALS': '=',
    'NOT_EQUALS': '≠',
    'BETWEEN': '↔',
    'NOT_BETWEEN': '↮',
    'CONTAINS': '∋',
    'NOT_CONTAINS': '∌',
    'STARTS_WITH': '^...',
    'ENDS_WITH': '...$',
    'IS_NULL': '∅',
    'IS_NOT_NULL': '∃',
    'IN_LIST': 'in',
    'NOT_IN_LIST': 'not in',
  };
  return operationMap[operation.toUpperCase()] || operation;
};

// Text-based operators need smaller font size
const isTextBasedOperator = (operation: string): boolean => {
  const textOperators = ['IN_LIST', 'NOT_IN_LIST', 'STARTS_WITH', 'ENDS_WITH'];
  return textOperators.includes(operation.toUpperCase());
};

const getOperationText = (operation: string): string => {
  const operationTextMap: Record<string, string> = {
    'GREATER_THAN': 'Greater Than',
    'GREATER_THAN_OR_EQUAL': 'Greater Than or Equal',
    'LESS_THAN': 'Less Than',
    'LESS_THAN_OR_EQUAL': 'Less Than or Equal',
    'EQUALS': 'Equals',
    'NOT_EQUALS': 'Not Equals',
    'BETWEEN': 'Between',
    'NOT_BETWEEN': 'Not Between',
    'CONTAINS': 'Contains',
    'NOT_CONTAINS': 'Does Not Contain',
    'STARTS_WITH': 'Starts With',
    'ENDS_WITH': 'Ends With',
    'IS_NULL': 'Is Null',
    'IS_NOT_NULL': 'Is Not Null',
    'IN_LIST': 'In List',
    'NOT_IN_LIST': 'Not In List',
  };
  return operationTextMap[operation.toUpperCase()] || operation;
};

// Helper to check if operation is list-based
const isListOperation = (operation: string): boolean => {
  const op = operation.toUpperCase();
  return op === 'IN_LIST' || op === 'NOT_IN_LIST';
};

// Component to display list name for list operations
const ListValueDisplay = ({ listId }: { listId: string }) => {
  const { data, loading, error } = useGetListSummaryQuery({
    variables: { id: listId },
  });

  if (loading) {
    return (
      <Text size="1" color="gray">
        Loading...
      </Text>
    );
  }

  if (error || !data?.list) {
    return (
      <Badge variant="surface" color="gray">
        Unknown List
      </Badge>
    );
  }

  const { name, isSystem } = data.list;

  return (
    <Badge variant="surface" color={isSystem ? 'red' : 'blue'}>
      {name}
    </Badge>
  );
};

const formatValue = (value: FingerprintValue): string | JSX.Element => {
  if (value.value === null || value.value === undefined) return 'null';

  // Handle MonetaryValue objects
  if (typeof value.value === 'object' && 'amount' in value.value && 'currency' in value.value) {
    return (
      <MonetaryValueDisplay
        value={value.value as { amount: number; currency: ValueType }}
        variant="default"
      />
    );
  }

  if (typeof value.value === 'object') return JSON.stringify(value.value);
  return String(value.value);
};

const SignalTypeBadge = ({ signalType }: { signalType: string }) => {
  const config: Record<
    string,
    { color: 'blue' | 'purple' | 'orange'; label: string }
  > = {
    TRANSACTION: { color: 'blue', label: 'Transaction' },
    EVENT: { color: 'purple', label: 'Event' },
    BLOCK: { color: 'orange', label: 'Block' },
  };

  const { color, label } = config[signalType] || {
    color: 'blue',
    label: signalType,
  };

  return (
    <Badge color={color} variant="soft" size="2">
      {label}
    </Badge>
  );
};

const SignalDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const { data, loading, error } = useGetSignalQuery({
    variables: { id: id || '' },
    skip: !id,
  });

  const signal = data?.signal;

  if (loading) {
    return (
      <Flex align="center" justify="center" py="9">
        <Spinner size="3" />
      </Flex>
    );
  }

  if (error) {
    return (
      <Flex align="center" justify="center" py="9">
        <Text color="red">Error loading signal: {error.message}</Text>
      </Flex>
    );
  }

  if (!signal) {
    return (
      <Flex align="center" justify="center" direction="column" gap="4" py="9">
        <Text size="4" color="gray">
          Signal not found
        </Text>
        <Button variant="soft" onClick={() => navigate('/signals')}>
          <ArrowLeftIcon />
          Back to Signals
        </Button>
      </Flex>
    );
  }

  // Get trigger data from signal
  const triggers = signal.triggers;
  const totalTriggers = triggers?.totalCount ?? 0;
  const recentTriggers = triggers?.edges ?? [];
  const lastTriggered = recentTriggers.length > 0 ? recentTriggers[0].node.triggeredAt : null;
  const isActive = true; // TODO: Add status field to Signal when backend supports it

  return (
    <DetailPageLayout
      backPath="/signals"
      backText="Back to Signals"
      icon={<TargetIcon width={24} height={24} />}
      iconBackgroundColor="var(--purple-3)"
      iconColor="var(--purple-11)"
      title={signal.name}
      subtitle={signal.description}
      badge={<SignalTypeBadge signalType={signal.signalType} />}
      actions={
        <Flex gap="2">
          <Button variant="soft" color="gray" onClick={() => navigate('/signals/edit/' + signal.id)}>
            Edit Signal
          </Button>
          <Button variant="solid" onClick={() => setTestDialogOpen(true)}>
            Test Signal
          </Button>
        </Flex>
      }
    >
      <Box>

        {/* Status Banner - Full Width */}
        <Box
          mb="4"
          style={{
            backgroundColor: 'var(--gray-2)',
            marginLeft: 'calc(-12px - var(--space-1))',
            marginRight: 'calc(-12px - var(--space-1))',
          }}
        >
          <Box px="4" py="4">
            <Flex
              align="center"
              justify="between"
              gap="6"
              style={{ maxWidth: '100%' }}
            >
              <Flex align="center" gap="3" style={{ flex: '1' }}>
                <Box
                  style={{
                    padding: '10px',
                    borderRadius: '6px',
                    backgroundColor: 'var(--gray-1)',
                    border: '1px solid var(--gray-6)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CalendarIcon width={18} height={18} />
                </Box>
                <Flex direction="column" gap="1">
                  <Text size="1" weight="medium" color="gray">
                    Created
                  </Text>
                  <Text size="2" weight="bold">
                    <TimeAgoTooltip value={signal.createdAt} />
                  </Text>
                </Flex>
              </Flex>

              <Flex align="center" gap="3" style={{ flex: '1' }}>
                <Box
                  style={{
                    padding: '10px',
                    borderRadius: '6px',
                    backgroundColor: 'var(--gray-1)',
                    border: '1px solid var(--gray-6)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <ActivityLogIcon width={18} height={18} />
                </Box>
                <Flex direction="column" gap="1">
                  <Text size="1" weight="medium" color="gray">
                    Last Triggered
                  </Text>
                  <Text size="2" weight="bold">
                    {lastTriggered ? (
                      <TimeAgoTooltip value={lastTriggered} />
                    ) : (
                      <Text color="gray">Never</Text>
                    )}
                  </Text>
                </Flex>
              </Flex>

              <Flex align="center" gap="3" style={{ flex: '1' }}>
                <Box
                  style={{
                    padding: '10px',
                    borderRadius: '6px',
                    backgroundColor: 'var(--gray-1)',
                    border: '1px solid var(--gray-6)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <ActivityLogIcon width={18} height={18} />
                </Box>
                <Flex direction="column" gap="1">
                  <Text size="1" weight="medium" color="gray">
                    Total Triggers
                  </Text>
                  <Text size="2" weight="bold">
                    {totalTriggers}
                  </Text>
                </Flex>
              </Flex>

              <Flex align="center" gap="3" style={{ flex: '1' }}>
                <Box
                  style={{
                    padding: '10px',
                    borderRadius: '6px',
                    backgroundColor: 'var(--gray-1)',
                    border: '1px solid var(--gray-6)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CheckCircledIcon width={18} height={18} />
                </Box>
                <Flex direction="column" gap="1">
                  <Text size="1" weight="medium" color="gray">
                    Status
                  </Text>
                  <Text size="2" weight="bold" color={isActive ? 'green' : 'gray'}>
                    {isActive ? 'Active' : 'Inactive'}
                  </Text>
                </Flex>
              </Flex>
            </Flex>
          </Box>
        </Box>

        {/* Fingerprints Section */}
        <Box mb="4">
          <Card size="2">
            <Flex align="center" justify="between" mb="3">
              <Box>
                <Heading as="h2" size="4" weight="medium" mb="1">
                  Fingerprints
                </Heading>
                <Text size="2" color="gray">
                  Conditions that trigger this signal
                </Text>
              </Box>
              <Badge color="purple" variant="soft" size="2">
                {signal.fingerprints.length} Rule
                {signal.fingerprints.length !== 1 ? 's' : ''}
              </Badge>
            </Flex>
            <Box mb="3" style={{ marginLeft: 'calc(-1 * var(--space-4))', marginRight: 'calc(-1 * var(--space-4))', borderBottom: '1px solid var(--gray-6)' }} />

            {signal.fingerprints.length === 0 ? (
              <Flex align="center" justify="center" py="6">
                <Text color="gray">No fingerprints defined for this signal.</Text>
              </Flex>
            ) : (
              <Flex direction="column" gap="3">
                {signal.fingerprints.map((fingerprint: FingerprintUnion, index: number) => {
                  const fp = fingerprint;
                  const fpType = getFingerprintType(fp);
                  const fpField = getFingerprintField(fp);

                  return (
                    <Card
                      key={index}
                      style={{
                        backgroundColor: 'var(--gray-3)',
                        padding: '16px 20px',
                      }}
                    >
                      <Flex direction="column" gap="3">
                        {/* Conditions Row - spans full width */}
                        <Flex align="center" gap="6" style={{ width: '100%' }}>
                          {/* Field */}
                          <Box style={{ flex: '1', minWidth: 0 }}>
                            <Flex direction="column" gap="1" align="start">
                              <Text size="1" weight="medium" color="gray">
                                FIELD
                              </Text>
                              <Code size="2" weight="bold">
                                {fpField}
                              </Code>
                            </Flex>
                          </Box>

                          {/* Operation */}
                          <Box style={{ flex: '1', minWidth: 0 }}>
                            <Tooltip.Provider>
                              <Tooltip.Root>
                                <Tooltip.Trigger asChild>
                                  <Flex direction="column" gap="1" align="start" style={{ cursor: 'help' }}>
                                    <Text size="1" weight="medium" color="gray">
                                      OPERATION
                                    </Text>
                                    <Text
                                      size={isTextBasedOperator(fp.operation) ? '2' : '5'}
                                      weight="medium"
                                      style={{
                                        fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                                        fontVariantNumeric: 'tabular-nums',
                                      }}
                                    >
                                      {getOperationShorthand(fp.operation)}
                                    </Text>
                                  </Flex>
                                </Tooltip.Trigger>
                                <Tooltip.Portal>
                                  <Tooltip.Content
                                    side="top"
                                    style={{
                                      backgroundColor: 'var(--gray-12)',
                                      color: 'var(--gray-1)',
                                      padding: '6px 12px',
                                      borderRadius: '6px',
                                      fontSize: '14px',
                                      fontWeight: 500,
                                      maxWidth: '200px',
                                    }}
                                  >
                                    {getOperationText(fp.operation)}
                                    <Tooltip.Arrow style={{ fill: 'var(--gray-12)' }} />
                                  </Tooltip.Content>
                                </Tooltip.Portal>
                              </Tooltip.Root>
                            </Tooltip.Provider>
                          </Box>

                          {/* Threshold Value */}
                          <Box style={{ flex: '1', minWidth: 0 }}>
                            <Flex direction="column" gap="1" align="start">
                              <Text size="1" weight="medium" color="gray">
                                {isListOperation(fp.operation) ? 'LIST' : 'THRESHOLD VALUE'}
                              </Text>
                              {isListOperation(fp.operation) ? (
                                <ListValueDisplay listId={String(fp.value.value)} />
                              ) : typeof formatValue(fp.value) === 'string' ? (
                                <Code size="2" weight="bold" color="purple">
                                  {formatValue(fp.value)}
                                </Code>
                              ) : (
                                formatValue(fp.value)
                              )}
                            </Flex>
                          </Box>
                        </Flex>
                      </Flex>
                    </Card>
                  );
                })}
              </Flex>
            )}
          </Card>
        </Box>

        {/* Recent Matches Section */}
        <Box>
          <Card size="2">
            <Flex align="center" justify="between" mb="3">
              <Box>
                <Heading as="h2" size="4" weight="medium" mb="1">
                  Recent Triggers
                </Heading>
                <Text size="2" color="gray">
                  Transactions that triggered this signal
                </Text>
              </Box>
              <Box p="2">
                <Button
                  variant="ghost"
                  size="2"
                  onClick={() => navigate('./triggers')}
                >
                  View All
                </Button>
              </Box>
            </Flex>
            <Box mb="3" style={{ marginLeft: 'calc(-1 * var(--space-4))', marginRight: 'calc(-1 * var(--space-4))', borderBottom: '1px solid var(--gray-6)' }} />

            {/* Table with removed padding to sit flush */}
            <Box style={{ marginLeft: 'calc(-1 * var(--space-4))', marginRight: 'calc(-1 * var(--space-4))', marginBottom: 'calc(-1 * var(--space-4))' }}>
              {recentTriggers.length === 0 ? (
                <Flex align="center" justify="center" py="6">
                  <Text color="gray">No triggers yet for this signal.</Text>
                </Flex>
              ) : (
                <Table.Root size="2">
                  <Table.Header>
                    <Table.Row>
                      <Table.ColumnHeaderCell>Hash</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell>Time</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell>Block</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell>From</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell>To</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell>Value</Table.ColumnHeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {recentTriggers.map((edge, index) => {
                      const trigger = edge.node;
                      const tx = trigger.transaction;
                      return (
                        <Table.Row
                          key={trigger.id}
                          style={index === recentTriggers.length - 1 ? { borderBottom: 'none' } : undefined}
                        >
                          <Table.Cell>
                            {tx?.hash ? (
                              <HashShortener
                                hash={tx.hash}
                                link={`/transactions/${tx.hash}`}
                              />
                            ) : (
                              <Text color="gray">—</Text>
                            )}
                          </Table.Cell>
                          <Table.Cell>
                            <TimeAgoTooltip value={trigger.triggeredAt} />
                          </Table.Cell>
                          <Table.Cell>
                            <Text size="2">
                              {tx?.blockNumber?.toLocaleString() ?? '—'}
                            </Text>
                          </Table.Cell>
                          <Table.Cell>
                            {tx?.from?.address ? (
                              <HashShortener
                                hash={tx.from.address}
                                link={`/addresses/${tx.from.address}`}
                              />
                            ) : (
                              <Text color="gray">—</Text>
                            )}
                          </Table.Cell>
                          <Table.Cell>
                            {tx?.to?.address ? (
                              <HashShortener
                                hash={tx.to.address}
                                link={`/addresses/${tx.to.address}`}
                              />
                            ) : (
                              <Text color="gray">—</Text>
                            )}
                          </Table.Cell>
                          <Table.Cell>
                            {tx?.value ? (
                              <Text size="2">{tx.value}</Text>
                            ) : (
                              <Text color="gray">—</Text>
                            )}
                          </Table.Cell>
                        </Table.Row>
                      );
                    })}
                  </Table.Body>
                </Table.Root>
              )}
            </Box>
          </Card>
        </Box>
      </Box>

      <TestSignalDialog
        open={testDialogOpen}
        onOpenChange={setTestDialogOpen}
        signalId={signal.id}
        signalName={signal.name}
        signalType={signal.signalType}
      />
    </DetailPageLayout>
  );
};

export default SignalDetail;
